import { useUser } from "@clerk/nextjs";

import GoogleAutocomplete from "@/components/GoogleAutocomplete";
import LoadingButton from "@/components/LoadingButton";
import { StepHeader } from "@/components/onboarding/StepHeader";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { api, handleApiRequest } from "@/lib/utils";

import type { OnboardingStepProps } from "@/types/onboarding";
import type { SerpAPI } from "@/types/serpapi";

export function CompanySearchStep({ form, onNext }: OnboardingStepProps) {
  const { user } = useUser();

  const onSearch = async (data: { companyName: string; city: string; state: string }) =>
    handleApiRequest({
      makeRequest: async () => {
        const res: { data: SerpAPI.PlaceResult[] } = await api.get("/api/places/search", {
          params: {
            companyName: data.companyName,
            city: data.city,
            state: data.state,
          },
        });

        form.setValue(
          "serpApiData",
          res.data.map((item) => ({
            placeId: item.place_id,
            name: item.title,
            address: item.address,
            website: item.website,
          }))
        );

        onNext();
      },
    });

  return (
    <div className="h-full flex flex-col justify-start lg:justify-center px-5 lg:px-40 py-6 lg:py-0 w-full">
      <StepHeader
        title={
          <>
            Hi {user?.firstName} 👋 <br /> Let&apos;s find your business
          </>
        }
        subtitle="Save time by linking your Google Business Profile. Don't have one? Skip for now."
      />

      <div className="mt-6 lg:mt-12 w-full flex flex-col lg:gap-9 gap-6">
        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormItem className="flex flex-col mb-0">
              <FormLabel className="text-ash-700 font-semibold lg:text-base text-sm">Company Name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="text"
                  placeholder="Company Name"
                  className="text-sm mb-0 h-fit px-4 py-3 rounded-xl border-ash-300 bg-white"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem className="flex flex-col mb-0">
              <FormLabel className="text-ash-700 font-semibold lg:text-base text-sm">
                City, County, or Neighborhood
              </FormLabel>
              <FormControl>
                <GoogleAutocomplete
                  value={field.value}
                  onChange={field.onChange}
                  inputClassName="text-sm mb-0 h-fit px-4 py-3 rounded-xl border-ash-300 bg-white ring-0"
                  placeholder="City, County, or Neighborhood"
                  onPlaceChanged={(place) => {
                    form.setValue("location", place.formattedAddress, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                    form.setValue("city", place.cityCountyNeighborhood, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                    form.setValue("state", place.state, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {/* buttons should stack on mobile and be side by side on desktop */}
      <div className="flex gap-4 lg:mt-12 mt-auto">
        <LoadingButton
          type="button"
          variant="rebolt-secondary"
          size="md"
          onClick={async () => {
            const isValid = await form.trigger(["companyName", "city", "state"]);
            if (isValid) {
              return onSearch({
                companyName: form.getValues("companyName"),
                city: form.getValues("city"),
                state: form.getValues("state"),
              });
            }
          }}
          className="justify-center lg:max-w-fit tracking-tight lg:py-3 py-2"
        >
          Find my company
        </LoadingButton>
      </div>
    </div>
  );
}
